import type { ToastOptions } from '@common/hooks';
import { getSubDomain } from '@core/utils';
import * as Sentry from '@sentry/nextjs';
import { useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { AuthApi } from '@/api';
import { SESSION_KEYS_CLEARED_ON_LOGOUT, SESSION_KEYS_CLEARED_ON_USER_LOGOUT, STORAGE_KEY, SUB_DOMAIN } from '@/constants';
import { useAuthStore } from '@/stores/AuthStore';
import { switchMyPageUser } from '@/stores/useMyPageStore';
import { useAccessToken } from './useAccessToken';
interface LogoutOptions {
  redirect?: string;
  forInit?: boolean;
  toastOptions?: ToastOptions;
}
export const useLogout = () => {
  const {
    setAccessToken,
    setRefreshToken
  } = useAccessToken();
  const [setMyInfo, setIsDisabledAccessControl] = useAuthStore(useShallow(state => [state.setMyInfo, state.setIsDisabledAccessControl]));
  const {
    current
  } = useRef({
    logout: async (options?: LogoutOptions) => {
      const {
        redirect,
        forInit,
        toastOptions
      } = options ?? {};

      //* 로그인 사용자의 조회·인증 결과는 다음 사용자에게 이어지지 않도록 언제나 비운다.
      //* 정리 대상은 storage-key.ts에서 관리한다(호출부 나열 방식은 신규 키를 계속 누락시켰다).
      SESSION_KEYS_CLEARED_ON_LOGOUT.forEach(key => sessionStorage.removeItem(key));

      //* 진행 중인 가입 상태는 사용자가 실제로 로그아웃했을 때만 비운다.
      //* forInit은 "토큰이 없어 초기화"라, 비로그인으로 진행되는 가입 중에도 화면을 옮길 때마다 실행된다.
      //* 여기서 지우면 본인인증 왕복 후 가입 세션을 잃는다.
      if (!forInit) {
        SESSION_KEYS_CLEARED_ON_USER_LOGOUT.forEach(key_0 => sessionStorage.removeItem(key_0));
      }
      if (toastOptions) {
        sessionStorage.setItem(STORAGE_KEY.LOGOUT_TOAST, JSON.stringify(toastOptions));
      }
      if (!forInit) {
        setIsDisabledAccessControl(true);

        // 서버에 로그아웃 요청 완료 후 페이지 이동
        try {
          await AuthApi['post:/wello/v2/auth/member-oauth/logout']();
        } catch {
          // 실패해도 클라이언트 로그아웃 진행
        }
      }
      setAccessToken(null);
      setRefreshToken(null);
      setMyInfo(null);
      switchMyPageUser(null);
      Sentry.setUser(null);
      if (forInit || getSubDomain() === SUB_DOMAIN.KB_PAY) return;
      if (redirect) {
        return location.replace(redirect);
      }
      return location.reload();
    }
  });

  // eslint-disable-next-line react-hooks/refs -- 렌더마다 stable한 logout 함수 객체를 반환하기 위해 ref.current를 반환하는 의도적 패턴
  return current;
};